/** Shared card dimensions for step nodes and transition cards. */
export const CARD_W = 200
export const CARD_H = 88

/** Horizontal space between step cards; transitions sit on a separate row and may extend under the gap. */
export const GAP_W = 64

/** Inset from transition card corners so connectors don't clip the border stroke. */
export const CORNER_INSET = 12

/** Horizontal anchors on step bottom edge (fraction of card width). */
export const STEP_IN_X_RATIO = 0.38
export const STEP_OUT_X_RATIO = 0.62

/** Vertical space between the step row and transition row. */
export const ROW_GAP = 48

export const NODE_W = CARD_W
export const TRANS_W = CARD_W

export function benchWidth(stepCount: number): number {
    if (stepCount <= 0) {
        return 0
    }
    return stepCount * CARD_W + (stepCount - 1) * GAP_W
}

export function nodeLeft(index: number): number {
    return index * (CARD_W + GAP_W)
}

export function transitionLeft(index: number): number {
    return nodeLeft(index) + CARD_W + (GAP_W - CARD_W) / 2
}

export function nodeSlotWidth(index: number, stepCount: number): number {
    return index < stepCount - 1 ? CARD_W + GAP_W : CARD_W
}

export function benchHeight(): number {
    return CARD_H + ROW_GAP + CARD_H
}

export function transitionLayerTop(): number {
    return CARD_H + ROW_GAP
}

export interface LayoutPoint {
    x: number
    y: number
}

export function stepBottomIn(index: number): LayoutPoint {
    return {
        x: nodeLeft(index) + CARD_W * STEP_IN_X_RATIO,
        y: CARD_H,
    }
}

export function stepBottomOut(index: number): LayoutPoint {
    return {
        x: nodeLeft(index) + CARD_W * STEP_OUT_X_RATIO,
        y: CARD_H,
    }
}

export function transitionTopLeft(index: number): LayoutPoint {
    return {
        x: transitionLeft(index) + CORNER_INSET,
        y: transitionLayerTop(),
    }
}

export function transitionTopRight(index: number): LayoutPoint {
    return {
        x: transitionLeft(index) + CARD_W - CORNER_INSET,
        y: transitionLayerTop(),
    }
}

/** Smooth cubic curve between two anchor points. */
export function cubicPath(start: LayoutPoint, end: LayoutPoint): string {
    const bend = (end.y - start.y) * 0.55
    return `M ${start.x} ${start.y} C ${start.x} ${start.y + bend}, ${end.x} ${
        end.y - bend
    }, ${end.x} ${end.y}`
}

/** Curved path from step bottom-out to transition top-left. */
export function buildStepToTransitionPath(index: number): string {
    return cubicPath(stepBottomOut(index), transitionTopLeft(index))
}

/** Curved path from transition top-right to next step bottom-in. */
export function buildTransitionToStepPath(index: number): string {
    return cubicPath(transitionTopRight(index), stepBottomIn(index + 1))
}
